import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import orangeLogo from '../assets/orangelog.png';

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        q: 'What is PubSilo?',
        a: 'PubSilo is an Article Credit-based journalism marketplace. Readers use Article Credits to unlock individual articles from publishers — no subscriptions required. Publishers earn revenue from every article purchase with full editorial independence and transparent monthly payouts.',
      },
      {
        q: 'How do I create a free account?',
        a: 'Click "Create Free Account" on the homepage. After submitting your details, check your inbox for a verification email. Once you click the link, your account is active and you will receive 25 free Article Credits — no payment information required to get started.',
      },
      {
        q: 'Is PubSilo free to join as a reader?',
        a: 'Yes. Creating a reader account is completely free. Every new account receives 25 free Promotional Credits so you can start reading right away. Additional Article Credits are purchased in packages whenever you need them.',
      },
      {
        q: 'How do I sign up as a publisher?',
        a: 'Publishers go through a brief onboarding review. Submit your company information using the "Become a Publisher" form on our contact page. Our team will review your application and reach out to complete your setup, giving you access to the full company dashboard including author and editor management.',
      },
    ],
  },
  {
    id: 'article-credits',
    title: 'Article Credits & Billing',
    items: [
      {
        q: 'What are PubSilo Article Credits?',
        a: 'Article Credits are PubSilo\'s virtual currency. Each credit is worth $0.01 USD. You spend them to access articles — for example, a 25-credit article costs $0.25. Article Credits are purchased in bundles and stored in your account balance until you use them.',
      },
      {
        q: 'How do I buy more Article Credits?',
        a: 'From your dashboard, click "Buy Article Credits." Two packages are available: 500 credits for $6 and 900 credits for $10. Your total balance (purchased + promotional) cannot exceed 2,000 credits ($20.00).',
      },
      {
        q: 'Do my Article Credits expire?',
        a: 'No. Purchased Article Credits never expire. Read at your own pace — your balance stays in your account until you choose to use it.',
      },
      {
        q: 'What are Promotional Credits?',
        a: 'Promotional Credits are bonus credits issued by PubSilo — such as the 25 credits you receive on signup, or credits refunded when a publisher removes an article you already purchased. Promotional Credits have the same reading power as purchased credits but carry no cash value and cannot be withdrawn or converted.',
      },
      {
        q: 'Can I get a refund on Article Credit purchases?',
        a: 'Article Credit package purchases are final. However, if a publisher removes an article you have already purchased, PubSilo automatically issues a Promotional Credit refund equal to the article\'s credit cost at the time of deletion.',
      },
      {
        q: 'What happens if an article\'s price changes after I buy it?',
        a: 'Nothing changes for you. Once you purchase access to an article, you retain that access permanently regardless of any future price changes to that article.',
      },
    ],
  },
  {
    id: 'reading',
    title: 'Reading & Discovering Content',
    items: [
      {
        q: 'How do I find articles?',
        a: 'Articles surface in several places: your personalized "For You" feed, genre category feeds (Local News, Sports, Technology, and more), and individual publisher pages. Use the genre navigation bar at the top to browse by topic or region.',
      },
      {
        q: 'What is the "For You" feed?',
        a: 'The For You feed is a personalized stream built from your interests, location, and reading history. You can update your interests at any time from the Settings panel (gear icon) in your dashboard.',
      },
      {
        q: 'Can I re-read articles I have already purchased?',
        a: 'Yes. Every article you purchase is saved to your reading history. You can return to it at any time without spending additional Article Credits.',
      },
      {
        q: 'What does "Report Broken Link" do?',
        a: 'Some articles are hosted on the publisher\'s own website and redirect there after purchase. If that page has moved or been removed, use the "Report Broken Link" option in the article menu to alert our team. After enough reports are received on the same article, our admin team investigates and issues Promotional Credit refunds to affected readers if confirmed.',
      },
    ],
  },
  {
    id: 'publishers',
    title: 'For Publishers',
    items: [
      {
        q: 'What types of content does PubSilo accept?',
        a: 'PubSilo is built for journalism, editorial writing, investigative reporting, analysis, and niche newsletters. Content must be original, factually grounded, and free of spam, plagiarism, or deliberate misinformation. Our team reviews all publishers before approval and monitors content standards after onboarding.',
      },
      {
        q: 'Can I host articles on my own website and still earn through PubSilo?',
        a: 'Yes, and this is one of PubSilo\'s most flexible features. You have three options: host the full article on PubSilo and let us handle delivery; use an external link where readers purchase on PubSilo and are redirected to your own page afterward, keeping your traffic and analytics intact; or mix both approaches across different articles. The choice is yours on a per-article basis.',
      },
      {
        q: 'What is the auto-pricing engine?',
        a: 'Auto-pricing is an optional feature that gradually reduces an article\'s token cost over time based on its publish date and purchase volume. You set the starting price and a minimum floor — the engine handles the rest. Newer articles stay at full price while older content becomes more accessible, squeezing more long-term revenue from your catalog. You can override or lock any article to manual pricing at any time.',
      },
      {
        q: 'How does the revenue split work?',
        a: 'PubSilo deducts a platform fee from each article purchase. The remainder is credited to your account. Your monthly invoice provides a full breakdown by article, price point, purchase count, platform fees, and net payout — with complete line-item transparency.',
      },
      {
        q: 'When do I get paid?',
        a: 'Publisher payouts are processed monthly by our admin team. You will receive a notification when your invoice is settled. Payouts may be held if your account carries an outstanding deletion debt or is under review.',
      },
      {
        q: 'Can I add authors and editors to my account?',
        a: 'Yes. From your Company Dashboard you can add authors to write and submit articles, and editors to review and approve content before it goes live. Each role has a dedicated dashboard with the tools relevant to their workflow.',
      },
      {
        q: 'What is a deletion debt?',
        a: 'If you delete an article that was purchased by readers in a billing period that has already been paid out, PubSilo issues Promotional Credit refunds to those readers on your behalf. The dollar equivalent of those refunds becomes a deletion debt on your account, and future payouts are held until the debt is resolved. This system protects readers without requiring immediate cash repayment from you.',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account & Security',
    items: [
      {
        q: 'How do I reset my password?',
        a: 'On the login page, click "Forgot your password?" Enter your email address and we will send a reset link. The link expires within a short window for security — if it expires, simply request a new one from the same page.',
      },
      {
        q: 'How do I verify my email address?',
        a: 'After signing up, check your inbox for a verification email from PubSilo. Click the link inside to activate your account. If you do not see it within a few minutes, check your spam or junk folder. You can also use the "Resend" button on the waiting screen.',
      },
      {
        q: 'How do I update my reading interests?',
        a: 'From your dashboard, open the Settings panel using the gear icon. Click "Update Interests" to select the topics, genres, and regions that shape your For You feed. Changes take effect immediately.',
      },
      {
        q: 'Can I delete my account?',
        a: 'Yes. Email support@pubsilo.com with your account deletion request. We will process it within a reasonable timeframe. Note that remaining purchased Article Credit balances are non-refundable and forfeited upon voluntary deletion. Transaction records required for financial compliance are retained per our Privacy Policy.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact & Support',
    items: [
      {
        q: 'How do I contact support?',
        a: 'Email us at support@pubsilo.com. We aim to respond within one business day. For account or billing issues, include your username and a clear description of the problem.',
      },
      {
        q: 'How do I report a content or policy violation?',
        a: 'Use the reporting tools within the platform, or email content@pubsilo.com. For DMCA copyright takedown requests, include the URL of the original work, the URL of the infringing content on PubSilo, and your contact information.',
      },
      {
        q: 'How do I advertise on PubSilo?',
        a: 'Visit our Advertise page to submit an inquiry form. We will follow up with placement options, targeting details, and pricing based on your goals and budget.',
      },
    ],
  },
];

function AccordionItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #0e1a28' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 0', textAlign: 'left', gap: '16px',
        }}
      >
        <span style={{ fontSize: '15px', fontWeight: '600', color: open ? '#f0f4ff' : '#c8d8e8', fontFamily: 'Georgia, serif', lineHeight: '1.4' }}>
          {q}
        </span>
        <span style={{ fontSize: '22px', color: '#F41238', flexShrink: 0, lineHeight: 1, fontWeight: '300' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <div style={{ paddingBottom: '18px', fontSize: '14px', color: '#6a8aaa', lineHeight: '1.85', fontFamily: 'Arial, sans-serif' }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function HelpCenter() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
    <Helmet>
      <title>Help Center — PubSilo</title>
      <meta name="description" content="Answers to common questions about PubSilo — article credits, publishers, purchases, and more." />
    </Helmet>
    <div style={{ minHeight: '100vh', background: '#06090f', fontFamily: 'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{
        background: '#040710', borderBottom: '1px solid #0e1a28',
        padding: '14px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ background: '#F41238', borderRadius: '8px', padding: '7px 16px', display: 'inline-block' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '28px', objectFit: 'contain', display: 'block' }} />
          </div>
        </Link>
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#4a6a8a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Help Center
        </div>
        <Link to="/" style={{ fontSize: '13px', color: '#4a6a8a', textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
          onMouseLeave={e => e.currentTarget.style.color = '#4a6a8a'}
        >← Back to PubSilo</Link>
      </div>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(to bottom, #0d1320, #06090f)', padding: '56px 40px 44px', textAlign: 'center' }}>
        <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#4a6a8a', marginBottom: '12px' }}>
          Support
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: '900', color: '#f0f4ff', fontFamily: 'Georgia, serif', margin: '0 0 14px' }}>
          How can we help?
        </h1>
        <p style={{ fontSize: '15px', color: '#5a7a9a', maxWidth: '460px', margin: '0 auto 32px', lineHeight: '1.65' }}>
          Answers about Article Credits, reading, publishing, accounts, and more.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              style={{
                background: 'rgba(244,18,56,0.1)', border: '1px solid rgba(244,18,56,0.2)',
                borderRadius: '20px', padding: '7px 16px', cursor: 'pointer',
                fontSize: '12px', fontWeight: '700', color: '#c04060',
                fontFamily: 'Arial, sans-serif', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,18,56,0.2)'; e.currentTarget.style.color = '#F41238'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,18,56,0.1)'; e.currentTarget.style.color = '#c04060'; }}
            >
              {s.title}
            </button>
          ))}
        </div>
      </div>

      {/* Accordion sections */}
      <div style={{ maxWidth: '780px', margin: '0 auto', padding: '24px 40px 80px' }}>
        {SECTIONS.map(section => (
          <div key={section.id} id={section.id} style={{ marginBottom: '52px', scrollMarginTop: '80px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <div style={{ width: '3px', height: '22px', background: '#F41238', borderRadius: '2px', flexShrink: 0 }} />
              <h2 style={{ fontSize: '19px', fontWeight: '900', color: '#f0f4ff', fontFamily: 'Georgia, serif', margin: 0 }}>
                {section.title}
              </h2>
            </div>
            <div style={{ paddingLeft: '15px' }}>
              {section.items.map((item, i) => (
                <AccordionItem key={i} {...item} />
              ))}
            </div>
          </div>
        ))}

        {/* Contact card */}
        <div style={{
          background: 'rgba(244,18,56,0.06)', border: '1px solid rgba(244,18,56,0.18)',
          borderRadius: '12px', padding: '32px', textAlign: 'center', marginTop: '12px',
        }}>
          <div style={{ fontSize: '20px', fontWeight: '800', color: '#f0f4ff', fontFamily: 'Georgia, serif', marginBottom: '8px' }}>
            Still have questions?
          </div>
          <p style={{ fontSize: '14px', color: '#5a7a9a', margin: '0 0 20px', lineHeight: '1.65' }}>
            Our team is happy to help. Reach out and we will respond as soon as possible.
          </p>
          <a
            href="mailto:support@pubsilo.com"
            style={{
              display: 'inline-block', background: '#F41238', color: '#fff',
              padding: '11px 26px', borderRadius: '7px', textDecoration: 'none',
              fontSize: '14px', fontWeight: '700',
            }}
          >
            Email Support
          </a>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #0e1a28', padding: '20px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', background: '#040710' }}>
        <div style={{ fontSize: '13px', color: '#2a4060' }}>© {new Date().getFullYear()} PubSilo, LLC</div>
        <div style={{ display: 'flex', gap: '24px' }}>
          <Link to="/terms" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >Terms of Service</Link>
          <Link to="/privacy" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >Privacy Policy</Link>
          <Link to="/" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c8d8e8'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >← PubSilo</Link>
        </div>
      </div>
    </div>
    </>
  );
}
